import React from "react";
import { HomeworkHistoryAssignment } from "../api/contracts";

export interface CompletedHomeworkActivityTypeSlice {
  activityType: string;
  count: number;
  percentage: number;
}

const chartColors = [
  "#93c5fd",
  "#86efac",
  "#fcd34d",
  "#fda4af",
  "#c4b5fd",
  "#67e8f9",
  "#f0abfc",
  "#bef264",
  "#fdba74",
  "#a5b4fc",
  "#5eead4",
  "#d8b4fe",
] as const;

export function buildCompletedHomeworkActivityTypeSlices(
  assignments: readonly HomeworkHistoryAssignment[],
): CompletedHomeworkActivityTypeSlice[] {
  const counts = new Map<string, number>();

  for (const assignment of assignments) {
    if (
      assignment.completionStatus !== "Completed" &&
      assignment.completionStatus !== "Completed with Modification"
    ) {
      continue;
    }

    const activityType = assignment.activityType.trim() || "Unspecified";
    counts.set(activityType, (counts.get(activityType) ?? 0) + 1);
  }

  const total = [...counts.values()].reduce((sum, count) => sum + count, 0);
  return [...counts.entries()]
    .sort(([leftLabel, leftCount], [rightLabel, rightCount]) =>
      rightCount === leftCount
        ? leftLabel.localeCompare(rightLabel)
        : rightCount - leftCount,
    )
    .map(([activityType, count]) => ({
      activityType,
      count,
      percentage: total ? (count / total) * 100 : 0,
    }));
}

function pointAt(cx: number, cy: number, radius: number, angle: number) {
  const radians = (angle * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(radians),
    y: cy + radius * Math.sin(radians),
  };
}

function slicePath(
  cx: number,
  cy: number,
  radius: number,
  startAngle: number,
  endAngle: number,
): string {
  const start = pointAt(cx, cy, radius, startAngle);
  const end = pointAt(cx, cy, radius, endAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return [
    `M ${cx} ${cy}`,
    `L ${start.x} ${start.y}`,
    `A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y}`,
    "Z",
  ].join(" ");
}

function formatPercentage(value: number): string {
  return `${value.toFixed(1)}%`;
}

interface ChartSlice extends CompletedHomeworkActivityTypeSlice {
  color: string;
  startAngle: number;
  endAngle: number;
  labelAngle: number;
}

interface CalloutPosition {
  slice: ChartSlice;
  side: "left" | "right";
  y: number;
}

function buildChartSlices(
  slices: readonly CompletedHomeworkActivityTypeSlice[],
): ChartSlice[] {
  let currentAngle = -90;

  return slices.map((slice, index) => {
    const startAngle = currentAngle;
    const sweep = (slice.percentage / 100) * 360;
    const endAngle = startAngle + sweep;
    currentAngle = endAngle;

    return {
      ...slice,
      color: chartColors[index % chartColors.length],
      startAngle,
      endAngle,
      labelAngle: slices.length === 1 ? -25 : startAngle + sweep / 2,
    };
  });
}

function distributeCalloutPositions(
  slices: readonly ChartSlice[],
  cx: number,
  cy: number,
  radius: number,
): CalloutPosition[] {
  const minY = 26;
  const maxY = 334;
  const sides = (["left", "right"] as const).flatMap((side) => {
    const positioned = slices
      .filter((slice) => {
        const point = pointAt(cx, cy, radius, slice.labelAngle);
        return side === "right" ? point.x >= cx : point.x < cx;
      })
      .map((slice) => ({
        slice,
        side,
        targetY: pointAt(cx, cy, radius + 26, slice.labelAngle).y,
        y: 0,
      }))
      .sort((left, right) => left.targetY - right.targetY);
    const minimumGap =
      positioned.length > 1
        ? Math.min(30, (maxY - minY) / (positioned.length - 1))
        : 30;

    let previousY = minY - minimumGap;
    for (const item of positioned) {
      item.y = Math.max(minY, item.targetY, previousY + minimumGap);
      previousY = item.y;
    }

    if (positioned.length && positioned[positioned.length - 1].y > maxY) {
      positioned[positioned.length - 1].y = maxY;
      for (let index = positioned.length - 2; index >= 0; index -= 1) {
        positioned[index].y = Math.min(
          positioned[index].y,
          positioned[index + 1].y - minimumGap,
        );
      }
    }

    return positioned.map(({ slice, side: itemSide, y }) => ({
      slice,
      side: itemSide,
      y,
    }));
  });

  return sides;
}

function SliceShape({
  slice,
  slicesCount,
  cx,
  cy,
  radius,
}: {
  slice: ChartSlice;
  slicesCount: number;
  cx: number;
  cy: number;
  radius: number;
}) {
  const ariaLabel = `${slice.activityType}: ${slice.count} completed homework, ${formatPercentage(slice.percentage)}`;

  return slicesCount === 1 ? (
    <circle cx={cx} cy={cy} r={radius} fill={slice.color}>
      <title>{ariaLabel}</title>
    </circle>
  ) : (
    <path
      d={slicePath(cx, cy, radius, slice.startAngle, slice.endAngle)}
      fill={slice.color}
      stroke="#fff"
      strokeWidth="2"
    >
      <title>{ariaLabel}</title>
    </path>
  );
}

function SliceValue({
  slice,
  cx,
  cy,
  radius,
}: {
  slice: ChartSlice;
  cx: number;
  cy: number;
  radius: number;
}) {
  if (slice.percentage < 8) return null;

  const labelPoint =
    slice.percentage === 100
      ? { x: cx, y: cy }
      : pointAt(cx, cy, radius * 0.62, slice.labelAngle);

  return (
    <text
      className="cp-activity-type-chart__percentage"
      x={labelPoint.x}
      y={labelPoint.y - 2}
      textAnchor="middle"
      aria-hidden="true"
    >
      <tspan x={labelPoint.x}>{formatPercentage(slice.percentage)}</tspan>
      <tspan
        className="cp-activity-type-chart__slice-count"
        x={labelPoint.x}
        dy="15"
      >
        {slice.count}
      </tspan>
    </text>
  );
}

export function CompletedHomeworkByActivityTypeChart({
  slices,
}: {
  slices: readonly CompletedHomeworkActivityTypeSlice[];
}) {
  if (!slices.length) {
    return (
      <div className="cp-chart-empty">
        <h3>No completed homework yet</h3>
        <p>
          Completed homework will be grouped by activity type when it becomes
          available.
        </p>
      </div>
    );
  }

  const chartSlices = buildChartSlices(slices);
  const cx = 400;
  const cy = 180;
  const radius = 112;
  const callouts = distributeCalloutPositions(chartSlices, cx, cy, radius);

  return (
    <figure className="cp-activity-type-chart">
      <div
        className="cp-activity-type-chart__plot"
        role="img"
        aria-label="Completed homework grouped by activity type"
      >
        <svg
          className="cp-activity-type-chart__svg cp-activity-type-chart__svg--callouts"
          viewBox="0 0 800 360"
          aria-hidden="true"
        >
          {chartSlices.map((slice) => (
            <g key={slice.activityType}>
              <SliceShape
                slice={slice}
                slicesCount={chartSlices.length}
                cx={cx}
                cy={cy}
                radius={radius}
              />
              <SliceValue slice={slice} cx={cx} cy={cy} radius={radius} />
            </g>
          ))}
          {callouts.map(({ slice, side, y }) => {
            const start = pointAt(cx, cy, radius + 3, slice.labelAngle);
            const elbow = pointAt(cx, cy, radius + 24, slice.labelAngle);
            const lineEndX = side === "right" ? 612 : 228;
            const labelX = side === "right" ? 620 : 220;

            return (
              <g key={`callout-${slice.activityType}`}>
                <polyline
                  className="cp-activity-type-chart__callout-line"
                  points={`${start.x},${start.y} ${elbow.x},${elbow.y} ${lineEndX},${y}`}
                  style={{ stroke: slice.color }}
                />
                <text
                  className="cp-activity-type-chart__callout-label"
                  x={labelX}
                  y={slice.percentage < 8 ? y - 4 : y + 4}
                  textAnchor={side === "right" ? "start" : "end"}
                >
                  <tspan x={labelX}>{slice.activityType}</tspan>
                  {slice.percentage < 8 ? (
                    <tspan
                      className="cp-activity-type-chart__callout-detail"
                      x={labelX}
                      dy="15"
                    >
                      {slice.count} · {formatPercentage(slice.percentage)}
                    </tspan>
                  ) : null}
                </text>
              </g>
            );
          })}
        </svg>
        <svg
          className="cp-activity-type-chart__svg cp-activity-type-chart__svg--compact"
          viewBox="0 0 320 320"
          aria-hidden="true"
        >
          {chartSlices.map((slice) => (
            <g key={slice.activityType}>
              <SliceShape
                slice={slice}
                slicesCount={chartSlices.length}
                cx={160}
                cy={160}
                radius={radius}
              />
              {slice.percentage >= 8 ? (
                <text
                  className="cp-activity-type-chart__percentage"
                  x={
                    slice.percentage === 100
                      ? 160
                      : pointAt(160, 160, radius * 0.64, slice.labelAngle).x
                  }
                  y={
                    (slice.percentage === 100
                      ? 160
                      : pointAt(160, 160, radius * 0.64, slice.labelAngle).y) +
                    4
                  }
                  textAnchor="middle"
                  aria-hidden="true"
                >
                  {formatPercentage(slice.percentage)}
                </text>
              ) : null}
            </g>
          ))}
        </svg>
      </div>
      <ul className="cp-visually-hidden">
        {chartSlices.map((slice) => {
          const description = `${slice.activityType}: ${slice.count} completed homework, ${formatPercentage(slice.percentage)}`;
          return (
            <li key={slice.activityType} aria-label={description}>
              {description}
            </li>
          );
        })}
      </ul>
      <figcaption className="cp-activity-type-chart__legend">
        <strong>Activity Type</strong>
        <ul>
          {chartSlices.map((slice) => (
            <li key={slice.activityType}>
              <i aria-hidden="true" style={{ backgroundColor: slice.color }} />
              <span>{slice.activityType}</span>
              <b>{slice.count}</b>
            </li>
          ))}
        </ul>
      </figcaption>
    </figure>
  );
}
